import React, { useContext, useState, useEffect, createContext } from 'react';
import { auth } from './FirebaseConfig';
import { API_BASE } from '../apiSession';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!auth || typeof auth.onAuthStateChanged !== 'function') {
      setCurrentUser(null);
      setIsLoading(false);
      return undefined;
    }

    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  // The browser extension's line to a fresh login token, on every page of
  // the app. It holds the token it was handed with a fill or a list, and a
  // Firebase token lives an hour; on a 401 it asks an open app tab for a new
  // one (ASSISTALIEN_TOKEN_REQUEST). Only the Auto-apply page answered, so a
  // list that ran for an hour while the person was on another page had every
  // post refused, "Sent" included. This provider is mounted on every route,
  // so the answer comes from here and nowhere else — one answer per ask.
  useEffect(() => {
    if (!currentUser) return undefined;
    // Heard from the extension: its hello, or an ask. Without it there is
    // nobody to hand a token to, and the timer below costs a token refresh
    // every 45 minutes for nothing.
    let extHere = false;
    const push = async () => {
      try {
        const token = await currentUser.getIdToken(true);
        if (token) window.postMessage({ type: 'ASSISTALIEN_TOKEN', token, apiBase: API_BASE }, window.location.origin);
      } catch { /* offline or signed out: the extension's next call says so */ }
    };
    // The extension's own sign-in (2.2.7, extension worker/session.js), so
    // Auto-apply's ready jobs go out with this page closed: asked once per
    // page load whether the extension holds one for this person (only a yes
    // or a no comes back), and when it does not, this page's token is handed
    // over once, through the same doorway as every token push (bridge.js,
    // this origin only). The extension mints its own session with it: a
    // scoped, revocable one the server lets work the queue alone (Account →
    // Sign-in → Signed-in browsers). An older extension answers neither.
    let sessionAsked = false;
    let sessionHanded = false;
    const askSession = () => {
      if (sessionAsked) return;
      sessionAsked = true;
      window.postMessage({ type: 'ASSISTALIEN_SESSION_CHECK', uid: currentUser.uid }, window.location.origin);
    };
    const handSession = async () => {
      if (sessionHanded) return;
      sessionHanded = true;
      try {
        const token = await currentUser.getIdToken();
        if (token) window.postMessage({ type: 'ASSISTALIEN_SESSION', token, apiBase: API_BASE }, window.location.origin);
      } catch { /* offline or signed out: asked again on the next page load */ }
    };
    const onMsg = (e) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      const type = e.data?.type;
      if (type === 'ASSISTALIEN_EXT_READY') { extHere = true; askSession(); }
      else if (type === 'ASSISTALIEN_TOKEN_REQUEST') { extHere = true; push(); }
      else if (type === 'ASSISTALIEN_SESSION_STATE' && e.data.ok && !e.data.has) handSession();
    };
    window.addEventListener('message', onMsg);
    // The extension says hello when its script loads, which can be before
    // this listener was here: ask again.
    window.postMessage({ type: 'ASSISTALIEN_EXT_PING' }, window.location.origin);
    // A fresh one every 45 minutes, before the last one runs out, so a
    // long list rarely needs to ask at all.
    const t = setInterval(() => { if (extHere) push(); }, 45 * 60_000);
    return () => { window.removeEventListener('message', onMsg); clearInterval(t); };
  }, [currentUser]);

  const value = {
    currentUser,
    setCurrentUser,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};
